/**
 * Utility to detect and classify different types of AI messages
 */

export type MessageType =
  | 'form_request'
  | 'create_success'
  | 'update_success'
  | 'delete_success'
  | 'list_results'
  | 'search_results'
  | 'empty_state'
  | 'error'
  | 'interactive_card'
  | 'text_message'
  | 'workflow_status'
  | 'confirmation_request'
  | 'ticket_details'
  | 'greeting_response'
  | 'clarification_request'
  | 'analytics_ranking'
  | 'reasoning_pipeline'
  | 'tool_result'
  | 'general'; // fallback

export interface MessageTypeInfo {
  type: MessageType;
  structured_data?: any;
  metadata?: {
    actionName?: string;
    formFields?: string[];
    resultStatus?: 'success' | 'error' | 'warning';
    hasLinks?: boolean;
    colorHexes?: string[];
    listData?: any;
    totalCount?: number;
    entityType?: string;
  };
}

/**
 * Detects the type of message based on structured data and content patterns
 */
export function detectMessageType(content: string, structured_data?: any): MessageTypeInfo {
  // Priority 0: Check for tool_result from MCP backend
  if (structured_data?.type === 'tool_result' && structured_data?.formatted_response) {
    return {
      type: 'tool_result',
      structured_data,
      metadata: {
        tool_name: structured_data.tool_name,
        action_name: structured_data.action_name,
        result_mode: structured_data.formatted_response.mode
      }
    };
  }

  // Priority 1: Check for specific types in structured_data
  if (structured_data?.type === 'greeting_response') {
    return {
      type: 'greeting_response',
      structured_data,
      metadata: {
        hasLinks: content.includes('[') && content.includes(']('),
        colorHexes: extractHexColors(content)
      }
    };
  }

  if (structured_data?.type === 'clarification_request') {
    return {
      type: 'clarification_request',
      structured_data,
      metadata: {
        needs: structured_data.needs || [],
        suggestions: structured_data.suggestions || [],
        hasLinks: content.includes('[') && content.includes(']('),
        colorHexes: extractHexColors(content)
      }
    };
  }

  // Handle analytics_ranking response type
  if (structured_data?.type === 'analytics_ranking') {
    return {
      type: 'analytics_ranking',
      structured_data,
      metadata: {
        totalCount: structured_data.total_results || 0,
        entityType: structured_data.group_by_table || 'items',
        hasLinks: false,
        colorHexes: extractHexColors(content)
      }
    };
  }

  // Handle messages with no structured_data as simple text messages
  if (!structured_data || Object.keys(structured_data).length === 0) {
    if (content && content.trim().length > 0) {
      return {
        type: 'text_message',
        structured_data: null,
        metadata: {
          hasLinks: content.includes('[') && content.includes(']('),
          colorHexes: extractHexColors(content)
        }
      };
    }
  }
  
  // Handle linear_issues response type
  if (structured_data?.entity_type === 'linear_issues' && structured_data?.response_type === 'list') {
    // Helper functions for issue categorization
    const detectIssueType = (title: string): string => {
      if (title.includes('TypeError') || title.includes('Error:') || title.includes('AxiosError') || title.includes('UnhandledRejection')) {
        return 'Bug';
      }
      if (title.startsWith('Task ') && title.includes(':')) {
        return 'Task';
      }
      return 'Feature';
    };

    const categorizeIssue = (title: string): string => {
      // Detect specific error types
      if (title.includes('TypeError')) return 'TypeError';
      if (title.includes('AxiosError')) return 'Network Error';
      if (title.includes('UnhandledRejection')) return 'Promise Rejection';
      if (title.startsWith('Task ')) return 'Project Task';
      return 'Other';
    };

    const extractErrorType = (title: string): string | null => {
      // Extract specific error patterns
      if (title.includes('Cannot read properties of undefined')) return 'Undefined Property Access';
      if (title.includes('Cannot read properties of null')) return 'Null Property Access';
      if (title.includes('is not iterable')) return 'Iteration Error';
      if (title.includes('Network Error')) return 'Network Connectivity';
      if (title.includes('Cannot connect to the server')) return 'Server Connection';
      return null;
    };

    const getPriorityLabel = (priority: number): string => {
      if (priority === 0) return 'None';
      if (priority === 1) return 'Low';
      if (priority === 2) return 'Medium';
      if (priority === 3) return 'High';
      if (priority >= 4) return 'Urgent';
      return 'None';
    };

    const getStateColor = (state: string): string => {
      switch (state.toLowerCase()) {
        case 'triage': return '#f59e0b';
        case 'todo': return '#6b7280';
        case 'in progress': return '#3b82f6';
        case 'done': return '#10b981';
        case 'canceled': return '#ef4444';
        default: return '#6b7280';
      }
    };

    const transformedIssues = structured_data.items?.map((issue: any) => {
      const issueType = detectIssueType(issue.title);
      const category = categorizeIssue(issue.title);
      const errorType = extractErrorType(issue.title);
      const priorityLabel = getPriorityLabel(issue.priority);

      // Build subtitle with state and priority
      let subtitle = issue.state;
      if (issue.priority > 0) {
        subtitle += ` • ${priorityLabel} Priority`;
      }
      if (errorType) {
        subtitle += ` • ${errorType}`;
      }

      // Generate description based on issue type
      let description = null;
      if (issueType === 'Bug' && errorType) {
        description = `${errorType} - ${category}`;
      } else if (issueType === 'Task') {
        description = 'Project task requiring completion';
      }

      return {
        id: issue.id,
        title: issue.title,
        subtitle: subtitle,
        description: description,
        url: issue.url,
        createdAt: null, // Linear API doesn't provide creation time in this response
        metadata: {
          ...issue,
          issueType: issueType,
          category: category,
          errorType: errorType,
          state: issue.state,
          priority: issue.priority,
          priorityLabel: priorityLabel,
          state_color: getStateColor(issue.state),
          priority_color: issue.priority >= 3 ? '#ef4444' : issue.priority >= 2 ? '#f59e0b' : '#6b7280',
          isLinearIssue: true
        },
        // Linear issue-specific metadata for display
        state: issue.state,
        priority: issue.priority,
        issueType: issueType,
        linear_id: issue.id
      };
    }) || [];

    return {
      type: 'list_results',
      structured_data: {
        data: {
          items: transformedIssues,
          total_count: structured_data.total || transformedIssues.length,
          displayed_count: transformedIssues.length,
          entity_type: 'linear_issues',
          title: `Linear Issues (${structured_data.total || transformedIssues.length} found)`
        }
      },
      metadata: {
        listData: transformedIssues,
        totalCount: structured_data.total || transformedIssues.length,
        entityType: 'linear_issues',
        hasLinks: content.includes('[') && content.includes(']('),
        colorHexes: extractHexColors(content)
      }
    };
  }

  // Handle linear_teams response type
  if (structured_data?.entity_type === 'linear_teams' && structured_data?.response_type === 'list') {
    // Helper functions for team categorization
    const categorizeTeam = (teamName: string): string => {
      const name = teamName.toLowerCase();
      if (name.includes('product') || name.includes('design') || name.includes('ux')) {
        return 'Product';
      }
      if (name.includes('mcp') || name.includes('chimera') || name.includes('engineering') || name.includes('dev')) {
        return 'Engineering';
      }
      if (name.includes('marketing') || name.includes('sales') || name.includes('business')) {
        return 'Business';
      }
      return 'General';
    };

    const detectTeamPurpose = (teamName: string): string => {
      const category = categorizeTeam(teamName);
      switch (category) {
        case 'Product':
          return 'Product development and design team';
        case 'Engineering':
          return 'Engineering and development team';
        case 'Business':
          return 'Business and commercial team';
        default:
          return 'General purpose team';
      }
    };

    const getTeamTypeIcon = (category: string): string => {
      switch (category) {
        case 'Product': return '🚀';
        case 'Engineering': return '⚙️';
        case 'Business': return '💼';
        default: return '👥';
      }
    };

    const validateMemberCount = (reportedCount: number, actualMembers: any[]): boolean => {
      return reportedCount === (actualMembers?.length || 0);
    };

    const extractMemberDomains = (members: any[]): string[] => {
      return members
        .map(member => member.email?.split('@')[1])
        .filter(Boolean)
        .filter((domain, index, arr) => arr.indexOf(domain) === index);
    };

    const transformedTeams = structured_data.items?.map((team: any) => {
      const category = categorizeTeam(team.name);
      const memberCountValid = validateMemberCount(team.member_count, team.members);
      const memberDomains = extractMemberDomains(team.members || []);

      // Build subtitle with member count and validation
      let subtitle = `${team.member_count} member${team.member_count !== 1 ? 's' : ''}`;
      if (!memberCountValid) {
        subtitle += ` (${team.members?.length || 0} actual)`;
      }
      if (memberDomains.length > 0) {
        subtitle += ` • ${memberDomains.join(', ')}`;
      }

      // Generate description based on team type and composition
      let description = detectTeamPurpose(team.name);
      if (team.description && team.description.trim()) {
        description = team.description;
      }

      return {
        id: team.id,
        title: team.name,
        subtitle: subtitle,
        description: description,
        createdAt: null, // Linear API doesn't provide creation time in this response
        metadata: {
          ...team,
          teamType: category,
          teamIcon: getTeamTypeIcon(category),
          members: team.members || [],
          memberCount: team.member_count,
          actualMemberCount: team.members?.length || 0,
          memberCountValid: memberCountValid,
          memberDomains: memberDomains,
          key: team.key,
          isLinearTeam: true
        },
        // Linear team-specific metadata for display
        name: team.name,
        member_count: team.member_count,
        members: team.members || [],
        teamType: category,
        linear_team_id: team.id
      };
    }) || [];

    return {
      type: 'list_results',
      structured_data: {
        data: {
          items: transformedTeams,
          total_count: structured_data.total || transformedTeams.length,
          displayed_count: transformedTeams.length,
          entity_type: 'linear_teams',
          title: `Linear Teams (${structured_data.total || transformedTeams.length} found)`
        }
      },
      metadata: {
        listData: transformedTeams,
        totalCount: structured_data.total || transformedTeams.length,
        entityType: 'linear_teams',
        hasLinks: content.includes('[') && content.includes(']('),
        colorHexes: extractHexColors(content)
      }
    };
  }

  // Handle stripe_customers response type
  if (structured_data?.entity_type === 'stripe_customers' && structured_data?.response_type === 'list') {
    const transformedCustomers = structured_data.items?.map((customer: any) => {
      // Construct display name from Stripe customer data
      const displayTitle = customer.name || customer.email || `Customer ${customer.id}`;

      // Build subtitle with email and created date
      let displaySubtitle = '';
      if (customer.email && customer.email !== displayTitle) {
        displaySubtitle = customer.email;
      }
      if (customer.created) {
        const createdDate = new Date(customer.created * 1000).toLocaleDateString();
        displaySubtitle = displaySubtitle ? `${displaySubtitle} • Created ${createdDate}` : `Created ${createdDate}`;
      }

      return {
        id: customer.id,
        title: displayTitle,
        subtitle: displaySubtitle,
        description: customer.description || null,
        createdAt: customer.created ? new Date(customer.created * 1000).toISOString() : null,
        metadata: {
          ...customer,
          customerName: displayTitle,
          customerEmail: customer.email,
          stripeCustomerId: customer.id,
          created: customer.created,
          isStripeCustomer: true
        },
        // Stripe customer-specific metadata for display
        email: customer.email,
        name: customer.name,
        stripe_id: customer.id,
        created_timestamp: customer.created
      };
    }) || [];

    return {
      type: 'list_results',
      structured_data: {
        data: {
          items: transformedCustomers,
          total_count: structured_data.total || transformedCustomers.length,
          displayed_count: transformedCustomers.length,
          entity_type: 'stripe_customers',
          title: `Stripe Customers (${structured_data.total || transformedCustomers.length} found)`
        }
      },
      metadata: {
        listData: transformedCustomers,
        totalCount: structured_data.total || transformedCustomers.length,
        entityType: 'stripe_customers',
        hasLinks: content.includes('[') && content.includes(']('),
        colorHexes: extractHexColors(content)
      }
    };
  }

  // Handle customer_list response type
  if (structured_data?.type === 'customer_list') {
    const transformedCustomers = structured_data.customers?.map((customer: any) => {
      // Construct display name
      const displayTitle = `${customer.firstname || ''} ${customer.lastname || ''}`.trim() || customer.email || 'Unnamed Customer';
      
      // Build subtitle with company info
      let displaySubtitle = customer.email || '';
      if (customer.company) {
        displaySubtitle = displaySubtitle ? `${displaySubtitle} • ${customer.company}` : customer.company;
      }
      
      return {
        id: customer.id,
        title: displayTitle,
        subtitle: displaySubtitle,
        description: null, // Remove redundant company description since it's shown in company card
        url: customer.url,
        createdAt: customer.createdAt,
        metadata: {
          ...customer,
          customerName: displayTitle,
          customerEmail: customer.email,
          companyName: customer.company,
          companyId: customer.companyId,
          phone: customer.phone,
          isCustomer: customer.isCustomer
        },
        // Customer-specific metadata for display
        email: customer.email,
        company: customer.company,
        company_name: customer.company, // Add for company card display
        companyId: customer.companyId,
        phone: customer.phone,
        firstname: customer.firstname,
        lastname: customer.lastname
      };
    }) || [];
    
    return {
      type: 'list_results',
      structured_data: {
        data: {
          items: transformedCustomers,
          total_count: structured_data.total_count,
          entity_type: 'customers',
          title: `Customer Results (${structured_data.total_count} found)`
        }
      },
      metadata: {
        listData: transformedCustomers,
        totalCount: structured_data.total_count,
        entityType: 'customers',
        hasLinks: content.includes('[') && content.includes(']('),
        colorHexes: extractHexColors(content)
      }
    };
  }
  
  // Handle company_list response type
  if (structured_data?.type === 'company_list') {
    const transformedCompanies = structured_data.companies?.map((company: any) => {
      // Construct display title
      const displayTitle = company.name || 'Unnamed Company';
      
      // Build subtitle with website and customer count
      let displaySubtitle = '';
      if (company.website) {
        displaySubtitle = company.website;
      }
      if (company.customerCount > 0) {
        const customerText = `${company.customerCount} customer${company.customerCount !== 1 ? 's' : ''}`;
        displaySubtitle = displaySubtitle ? `${displaySubtitle} • ${customerText}` : customerText;
      }
      
      // Build description
      let description = '';
      if (company.website && company.customerCount > 0) {
        description = `Website: ${company.website} • ${company.customerCount} customers`;
      } else if (company.website) {
        description = `Website: ${company.website}`;
      } else if (company.customerCount > 0) {
        description = `${company.customerCount} customers`;
      }
      
      return {
        id: company.id,
        title: displayTitle,
        subtitle: displaySubtitle,
        description: description || company.description || null,
        url: company.url,
        createdAt: company.createdAt,
        metadata: {
          ...company,
          companyName: displayTitle,
          website: company.website,
          customerCount: company.customerCount,
          description: company.description
        },
        // Company-specific metadata for display
        name: company.name,
        website: company.website,
        customerCount: company.customerCount,
        company_name: company.name, // For search compatibility
        company_website: company.website
      };
    }) || [];
    
    return {
      type: 'list_results',
      structured_data: {
        data: {
          items: transformedCompanies,
          total_count: structured_data.total_count,
          entity_type: 'companies',
          title: `Company Results (${structured_data.total_count} found)`
        }
      },
      metadata: {
        listData: transformedCompanies,
        totalCount: structured_data.total_count,
        entityType: 'companies',
        hasLinks: content.includes('[') && content.includes(']('),
        colorHexes: extractHexColors(content)
      }
    };
  }
  
  // Handle team_list response type
  if (structured_data?.type === 'team_list') {
    const transformedTeams = structured_data.teams?.map((team: any) => {
      // Construct display title
      const displayTitle = team.name || 'Unnamed Team';
      
      // Parse member names and emails from string format
      let memberNames = [];
      let memberEmails = [];
      
      try {
        if (team.memberNames && typeof team.memberNames === 'string') {
          memberNames = team.memberNames.replace(/'/g, '"').includes('[') 
            ? JSON.parse(team.memberNames.replace(/'/g, '"'))
            : [team.memberNames];
        }
        if (team.memberEmails && typeof team.memberEmails === 'string') {
          memberEmails = team.memberEmails.replace(/'/g, '"').includes('[')
            ? JSON.parse(team.memberEmails.replace(/'/g, '"'))
            : [team.memberEmails];
        }
      } catch (e) {
        // Fallback parsing if JSON parsing fails
        if (team.memberNames) {
          memberNames = team.memberNames.split(',').map((name: string) => name.trim().replace(/[[\]']/g, ''));
        }
        if (team.memberEmails) {
          memberEmails = team.memberEmails.split(',').map((email: string) => email.trim().replace(/[[\]']/g, ''));
        }
      }
      
      // Build subtitle with member count and names
      let displaySubtitle = '';
      if (memberNames.length > 0) {
        const memberCount = memberNames.length;
        const memberText = `${memberCount} member${memberCount !== 1 ? 's' : ''}`;
        const memberList = memberNames.slice(0, 2).join(', ') + (memberNames.length > 2 ? '...' : '');
        displaySubtitle = `${memberText}: ${memberList}`;
      } else if (team.memberCount > 0) {
        displaySubtitle = `${team.memberCount} members`;
      }
      
      return {
        id: team.id,
        title: displayTitle,
        subtitle: displaySubtitle,
        description: team.description || null,
        url: team.url,
        createdAt: team.createdAt,
        metadata: {
          ...team,
          teamName: displayTitle,
          member_names: memberNames,
          member_emails: memberEmails,
          memberCount: memberNames.length || team.memberCount || 0,
          description: team.description
        },
        // Team-specific metadata for display
        name: team.name,
        memberNames: memberNames,
        memberEmails: memberEmails,
        memberCount: memberNames.length || team.memberCount || 0,
        member_names: memberNames, // For compatibility with existing team display logic
        member_emails: memberEmails
      };
    }) || [];
    
    return {
      type: 'list_results',
      structured_data: {
        data: {
          items: transformedTeams,
          total_count: structured_data.total_count,
          entity_type: 'teams',
          title: `Team Results (${structured_data.total_count} found)`
        }
      },
      metadata: {
        listData: transformedTeams,
        totalCount: structured_data.total_count,
        entityType: 'teams',
        hasLinks: content.includes('[') && content.includes(']('),
        colorHexes: extractHexColors(content)
      }
    };
  }
  
  if (structured_data?.type === 'data_list') {
    // Handle generic data_list (teams, contacts, customers, etc.)
    const transformedRows = structured_data.rows?.map((row: any) => {
      // Construct title based on entity type
      let displayTitle = row.title || row.name;
      let displaySubtitle = row.description;
      
      // For customers/contacts, use firstname and lastname
      if (structured_data.table === 'customers' || structured_data.table === 'contacts' || row.type === 'contact') {
        if (row.firstname || row.lastname) {
          displayTitle = `${row.firstname || ''} ${row.lastname || ''}`.trim();
        }
        // Fallback to email if no name
        if (!displayTitle && row.email) {
          displayTitle = row.email;
        }
        // Use email as subtitle if we have a name
        if (displayTitle && displayTitle !== row.email && row.email) {
          displaySubtitle = row.email;
        }
        // Add department or company info to subtitle if available
        if (row.department) {
          displaySubtitle = displaySubtitle ? `${displaySubtitle} • ${row.department}` : row.department;
        }
      }
      
      // For teams, use team name
      if (structured_data.table === 'teams') {
        displayTitle = row.name || displayTitle;
        // Show member count as subtitle if available
        if (row.member_names) {
          const memberCount = Array.isArray(row.member_names) ? row.member_names.length : 
                              typeof row.member_names === 'string' ? row.member_names.split(',').length : 0;
          if (memberCount > 0) {
            displaySubtitle = `${memberCount} member${memberCount !== 1 ? 's' : ''}`;
          }
        }
      }
      
      // Final fallback
      displayTitle = displayTitle || 'Untitled';
      
      return {
        ...row,
        title: displayTitle,
        subtitle: displaySubtitle || null,
        metadata: {
          ...row,
          // Clean up the metadata by removing complex objects
          officeHours: undefined,
          holidays: undefined,
          channels: undefined
        },
        // Add default actions based on table type
        actions: []
      };
    }) || [];
    
    return {
      type: 'list_results',
      structured_data: {
        data: {
          items: transformedRows,
          total_count: structured_data.total_count,
          entity_type: structured_data.table || 'items',
          title: `${structured_data.table || 'Data'} Results`
        }
      },
      metadata: {
        listData: transformedRows,
        totalCount: structured_data.total_count,
        entityType: structured_data.table || 'items',
        hasLinks: content.includes('[') && content.includes(']('),
        colorHexes: extractHexColors(content)
      }
    };
  }

  if (structured_data?.type === 'ticket_list') {
    // Transform tickets to match ListResultsMessage expected format
    const transformedTickets = structured_data.tickets?.map((ticket: any) => ({
      ...ticket,
      // Map direct properties to metadata for ListResultsMessage compatibility
      metadata: {
        status: ticket.status,
        priority: ticket.priority,
        assignedTo: ticket.assignedTo,
        assignedToName: ticket.assignedToName,
        customerId: ticket.customerId,
        customerName: ticket.customerName,
        customerEmail: ticket.customerEmail,
        teamNames: ticket.teamNames,
        created_at: ticket.createdAt,
        updated_at: ticket.updatedAt,
        // Add some color coding for status and priority
        status_color: ticket.status === 'Open' ? '#059669' : 
                     ticket.status === 'Closed' ? '#6b7280' : '#3b82f6',
        priority_color: ticket.priority === 'High' ? '#dc2626' :
                       ticket.priority === 'Medium' ? '#f59e0b' : '#10b981'
      },
      // Use actions from backend if provided, otherwise default actions
      actions: structured_data.actions || [
        { action: 'view_ticket_details', label: 'View Details', style: 'primary', icon: '👁️' },
        { action: 'assign_ticket_to_me', label: 'Assign to Me', style: 'outline', icon: '👤' },
        { action: 'add_ticket_comment', label: 'Add Comment', style: 'outline', icon: '💬' }
      ]
    })) || [];
    
    return {
      type: 'list_results',
      structured_data: {
        data: {
          items: transformedTickets,
          total_count: structured_data.total_count,
          entity_type: 'tickets',
          // Support global action links from backend
          action_links: structured_data.action_links || [
            { action: 'refresh_ticket_list', label: 'Refresh List', style: 'outline', icon: '🔄' },
            { action: 'create_new_ticket', label: 'Create New', style: 'primary', icon: '➕' }
          ]
        }
      },
      metadata: {
        listData: transformedTickets,
        totalCount: structured_data.total_count,
        entityType: 'tickets',
        hasLinks: content.includes('[') && content.includes(']('),
        colorHexes: extractHexColors(content)
      }
    };
  }

  // Handle linear_issue creation response type
  if (structured_data?.response_type === 'created' && structured_data?.entity_type === 'linear_issue') {
    // Helper functions for Linear URL processing
    const extractIssueNumber = (url: string): string | null => {
      const match = url.match(/\/issue\/([A-Z]+-\d+)\//);
      return match ? match[1] : null;
    };

    const extractWorkspace = (url: string): string | null => {
      const match = url.match(/linear\.app\/([^/]+)\//);
      return match ? match[1] : null;
    };

    const detectIssueCategory = (title: string): string => {
      const titleLower = title.toLowerCase();
      if (titleLower.includes('bug') || titleLower.includes('error') || titleLower.includes('fix')) {
        return 'Bug';
      }
      if (titleLower.includes('feature') || titleLower.includes('add') || titleLower.includes('implement')) {
        return 'Feature';
      }
      if (titleLower.includes('task') || titleLower.includes('update') || titleLower.includes('improve')) {
        return 'Task';
      }
      return 'General';
    };

    const getCategoryIcon = (category: string): string => {
      switch (category) {
        case 'Bug': return '🐛';
        case 'Feature': return '⚡';
        case 'Task': return '📋';
        default: return '📝';
      }
    };

    const issue = structured_data.data;
    const issueNumber = extractIssueNumber(issue.url || '');
    const workspace = extractWorkspace(issue.url || '');
    const category = detectIssueCategory(issue.title || '');

    return {
      type: 'create_success',
      structured_data: {
        ...structured_data,
        linear_issue: {
          ...issue,
          issueNumber: issueNumber,
          workspace: workspace,
          state: issue.state || 'Pending',
          category: category,
          categoryIcon: getCategoryIcon(category),
          isLinearIssue: true
        }
      },
      metadata: {
        resultStatus: 'success' as const,
        hasLinks: true,
        colorHexes: extractHexColors(content),
        entityType: 'linear_issue',
        actionType: 'create'
      }
    };
  }

  // Priority 2: Use structured data response_type if available (but skip if already handled above)
  if (structured_data?.response_type &&
      !(structured_data?.response_type === 'created' && structured_data?.entity_type === 'linear_issue')) {
    const responseType = structured_data.response_type;

    return {
      type: responseType as MessageType,
      structured_data,
      metadata: {
        listData: structured_data.data,
        totalCount: structured_data.data?.total_count,
        entityType: structured_data.data?.entity_type,
        hasLinks: content.includes('[') && content.includes(']('),
        colorHexes: extractHexColors(content)
      }
    };
  }

  // Priority 2: Fallback to content-based detection for legacy messages
  if (isTicketDetailsMessage(content)) {
    return {
      type: 'ticket_details',
      metadata: {
        hasLinks: content.includes('[') && content.includes(']('),
      }
    };
  }

  if (isFormMessage(content)) {
    return {
      type: 'form_request',
      metadata: {
        formFields: extractFormFields(content)
      }
    };
  }

  if (isActionResultMessage(content)) {
    return {
      type: 'create_success', // Assume create success for legacy action results
      metadata: {
        resultStatus: extractResultStatus(content),
        hasLinks: content.includes('[') && content.includes(']('),
        colorHexes: extractHexColors(content)
      }
    };
  }

  if (isErrorMessage(content)) {
    return {
      type: 'error',
      metadata: {
        resultStatus: 'error'
      }
    };
  }

  // Default to text message
  return { type: 'text_message' };
}

/**
 * Check if message contains ticket details information
 */
function isTicketDetailsMessage(content: string): boolean {
  const ticketIndicators = [
    '🎫', // Ticket emoji
    '**Details:**',
    '**Sno:**',
    '**Title:**',
    '**Entitytype:** ticket',
    '**Status:**',
    '**Priority:**',
    '**Client ID:**',
    '**Available Actions:**',
    '• Edit Ticket',
    '• Add Comment',
    '• Close Ticket'
  ];
  
  // Check if content has multiple ticket indicators (at least 3)
  const matchCount = ticketIndicators.filter(indicator => 
    content.includes(indicator)
  ).length;
  
  return matchCount >= 3;
}

/**
 * Check if message is a form-style response
 */
function isFormMessage(content: string): boolean {
  // Don't treat informational/help messages as forms
  if (content.includes('I can help you') || 
      content.includes('just say:') ||
      content.includes('What would you like to do?')) {
    return false;
  }

  const formIndicators = [
    'Please provide the following',
    'What would you like to name your team',
    '_________________',
    'Format Examples:',
    'Available Users:',
    'Available Email Channels:',
    'Available Chat Widgets:',
    'I\'ll help you set up'
  ];
  
  // Must have either explicit field patterns OR be a team/tag creation with available resources
  const hasFieldPatterns = content.includes('_________________') || 
                          content.includes('Please provide') ||
                          content.includes('What would you like to name');
                          
  const hasResourcesSection = content.includes('Available Users:') || 
                             content.includes('Available Email Channels:') ||
                             content.includes('Available Chat Widgets:');
  
  const isCreationForm = (content.includes('Create New Team') || content.includes('Create New Tag')) &&
                        (hasFieldPatterns || hasResourcesSection);
  
  return isCreationForm || formIndicators.some(indicator => 
    content.toLowerCase().includes(indicator.toLowerCase())
  );
}

/**
 * Check if message is an action result
 */
function isActionResultMessage(content: string): boolean {
  const resultIndicators = [
    '✅',
    '❌',
    '⚠️',
    'Successfully',
    'Created',
    'Updated',
    'Deleted',
    'Failed',
    'Error',
    'Complete',
    '**ID**:',
    '**Name**:',
    '**Status**:',
    'Tag Created',
    'Team Created',
    '**Team Name**:',
    '**Members**:',
    '**Description**:'
  ];
  
  return resultIndicators.some(indicator => content.includes(indicator));
}

/**
 * Check if message is an error
 */
function isErrorMessage(content: string): boolean {
  const errorIndicators = [
    'Sorry',
    'encountered an error',
    'Failed to',
    'Error:',
    'Could not',
    'Unable to'
  ];
  
  return errorIndicators.some(indicator => 
    content.toLowerCase().includes(indicator.toLowerCase())
  );
}

/**
 * Extract form field names from content
 */
function extractFormFields(content: string): string[] {
  const fields: string[] = [];
  const fieldPattern = /\*\*([^*]+):\*\*\s*_+/g;
  let match;
  
  while ((match = fieldPattern.exec(content)) !== null) {
    fields.push(match[1].trim());
  }
  
  return fields;
}

/**
 * Extract result status from content
 */
function extractResultStatus(content: string): 'success' | 'error' | 'warning' {
  if (content.includes('✅') || content.includes('Successfully')) {
    return 'success';
  }
  if (content.includes('❌') || content.includes('Failed') || content.includes('Error')) {
    return 'error';
  }
  if (content.includes('⚠️') || content.includes('Warning')) {
    return 'warning';
  }
  return 'success';
}

/**
 * Extract hex color codes from content
 */
export function extractHexColors(content: string): string[] {
  const hexPattern = /#[0-9A-Fa-f]{6}/g;
  const matches = content.match(hexPattern);
  return matches || [];
}